import React, { useEffect, useRef } from "react";

// Ingrandimento a due dita (1x–max) di un'anteprima SENZA cambiarne l'aspetto a 1x: due dita = scala attorno al
// punto toccato, un dito = spostamento (oltre i bordi fa scorrere il contenuto interno), doppio tocco = torna a 1x.
// `frameLoad` (contatore) riattacca i gesti anche dentro agli iframe figli same-origin.
export default function Zoomabile({ children, max = 4, frameLoad = 0, onScala, blocco = false, testid = "zoomabile" }) {
  const wrapRef = useRef(null);
  const innerRef = useRef(null);
  const st = useRef({ s: 1, tx: 0, ty: 0 });
  const cb = useRef(onScala);
  cb.current = onScala;

  useEffect(() => {
    const wrap = wrapRef.current;
    const inner = innerRef.current;
    if (!wrap || !inner) return;
    const clamp = (v, a, b) => Math.min(b, Math.max(a, v));
    const applica = () => {
      const { s, tx, ty } = st.current;
      inner.style.transform = s === 1 ? "" : `translate(${tx}px, ${ty}px) scale(${s})`;
      wrap.dataset.pinch = s.toFixed(2);
      cb.current?.(s);
    };
    const limita = () => {
      const c = st.current;
      const W = wrap.clientWidth;
      const H = wrap.clientHeight;
      c.tx = clamp(c.tx, W - W * c.s, 0);
      c.ty = clamp(c.ty, H - H * c.s, 0);
    };
    // coordinate del tocco rispetto al riquadro (anche se il tocco arriva da dentro un iframe scalato)
    const punto = (t) => {
      const r = wrap.getBoundingClientRect();
      let x = t.clientX;
      let y = t.clientY;
      const w = t.target?.ownerDocument?.defaultView;
      if (w && w !== window && w.frameElement) {
        const fr = w.frameElement.getBoundingClientRect();
        x = fr.left + x * st.current.s;
        y = fr.top + y * st.current.s;
      }
      return { x: x - r.left, y: y - r.top };
    };
    const dist = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);
    // primo contenitore che può scorrere risalendo dal tocco (anche fuori da un iframe e fuori dal riquadro)
    const scrollabile = (el) => {
      let n = el;
      while (n) {
        if (n.nodeType === 9) {
          const se = n.scrollingElement;
          if (n === document) return se;
          if (se && se.scrollHeight > se.clientHeight + 1) return se;
          n = n.defaultView?.frameElement || null;
          continue;
        }
        if (n.nodeType === 1) {
          const o = getComputedStyle(n).overflowY;
          if ((o === "auto" || o === "scroll") && n.scrollHeight > n.clientHeight + 1) return n;
        }
        n = n.parentNode;
      }
      return null;
    };

    let d0 = 0;
    let s0 = 1;
    let last = null;
    let lastTap = 0;
    const onStart = (e) => {
      if (e.touches.length === 2) {
        d0 = dist(punto(e.touches[0]), punto(e.touches[1]));
        s0 = st.current.s;
        last = null;
      } else if (e.touches.length === 1) {
        const now = Date.now();
        if (now - lastTap < 300 && st.current.s !== 1) {
          st.current = { s: 1, tx: 0, ty: 0 };
          applica();
        }
        lastTap = now;
        last = punto(e.touches[0]);
      }
    };
    const onMove = (e) => {
      const c = st.current;
      if (e.touches.length === 2 && d0) {
        e.preventDefault();
        const a = punto(e.touches[0]);
        const b = punto(e.touches[1]);
        const m = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
        const s = clamp((s0 * dist(a, b)) / d0, 1, max);
        c.tx = m.x - (m.x - c.tx) * (s / c.s);
        c.ty = m.y - (m.y - c.ty) * (s / c.s);
        c.s = s;
        if (s <= 1.01) st.current = { s: 1, tx: 0, ty: 0 };
        limita();
        applica();
      } else if (e.touches.length === 1 && last && c.s > 1) {
        e.preventDefault();
        const p = punto(e.touches[0]);
        const tx0 = c.tx + (p.x - last.x);
        const ty0 = c.ty + (p.y - last.y);
        last = p;
        c.tx = tx0;
        c.ty = ty0;
        limita();
        const sc = scrollabile(e.target);
        if (sc) {
          sc.scrollTop += (c.ty - ty0) / c.s;
          sc.scrollLeft += (c.tx - tx0) / c.s;
        }
        applica();
      }
    };
    const onEnd = (e) => {
      if (e.touches.length < 2) d0 = 0;
      last = e.touches.length === 1 ? punto(e.touches[0]) : null;
    };

    const bersagli = [wrap];
    wrap.querySelectorAll("iframe").forEach((f) => {
      try {
        const d = f.contentDocument;
        if (d?.body) {
          d.body.style.touchAction = "pinch-zoom pan-x pan-y";
          bersagli.push(d);
        }
      } catch (x) {
        console.debug("Zoomabile: iframe non accessibile", x);
      }
    });
    bersagli.forEach((b) => {
      b.addEventListener("touchstart", onStart, { passive: true });
      b.addEventListener("touchmove", onMove, { passive: false });
      b.addEventListener("touchend", onEnd, { passive: true });
      b.addEventListener("touchcancel", onEnd, { passive: true });
    });
    return () =>
      bersagli.forEach((b) => {
        b.removeEventListener("touchstart", onStart);
        b.removeEventListener("touchmove", onMove);
        b.removeEventListener("touchend", onEnd);
        b.removeEventListener("touchcancel", onEnd);
      });
  }, [max, frameLoad]);

  return (
    <div ref={wrapRef} className={`zoomabile ${blocco ? "blocco" : ""}`} data-testid={testid} data-pinch="1.00">
      <div ref={innerRef} className="zoomabile-in">{children}</div>
    </div>
  );
}
